import { memo, useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useGlobalStore } from '@highlight/ipc/react'
import { twMerge } from 'tailwind-merge'
import { isShownInViewport, toastManager, ToastItem } from './toast-store'
import { ToastPill } from './toast-pill'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { sidepanelVisibleWidth, useSidepanelRenderedWidth, useSidepanelSide } from '@renderer/overlay/Sidepanel/atoms'
import { useIsMiniContentHovered } from '@renderer/overlay/Mini/atoms'
import { useOverlayMenuBarHeight } from '@renderer/overlay/hooks/use-overlay-notch-bounds'
import { TOAST_VIEWPORT_PADDING, toastSidepanelShiftX } from './toast-offset'
import { useIsNotchShown } from './use-active-notch-toast'

/* -------------------------------- Constants ------------------------------- */

const DEFAULT_TOAST_DURATION = 10000
const ACTIVE_DEBOUNCE_MS = 150

const pillAlign = (pos: string) => (pos.includes('right') ? 'right' : pos.includes('center') ? 'center' : 'left')
const expandDir = (pos: string) => (pos.startsWith('top') ? 'bottom' : 'top')
// Duration is part of the key so `toastManager.setDuration` drops the old
// timer and reschedules with the new value (e.g. the action toast shortening
// itself once its actions succeed).
const timeoutKey = (t: ToastItem) => `${t.id}:${t.instanceId}:${t.duration ?? 'default'}`

/** In the notch, every toast, sticky ones included, is capped at the default duration unless it asked to persist. */
export function resolveToastDuration(
  { duration, persistent }: Pick<ToastItem, 'duration' | 'persistent'>,
  routesAllToNotch: boolean,
): number | null {
  if (persistent) return null
  if (routesAllToNotch) return Math.min(duration ?? DEFAULT_TOAST_DURATION, DEFAULT_TOAST_DURATION)
  if (duration === null) return null
  return duration ?? DEFAULT_TOAST_DURATION
}

/* -------------------------------- Component ------------------------------- */

export const ToastViewport = memo(function ToastViewport() {
  const [position] = useGlobalStore<string>('settings', 'toastPosition', 'top-right')
  // A shown notch takes every toast; with none, an undocked or signed-out Mini, they all stay in the corner
  const routesAllToNotch = useIsNotchShown()
  const isNotchHovered = useIsMiniContentHovered()
  const menuBarHeight = useOverlayMenuBarHeight()
  const [toasts, setToasts] = useState<Array<ToastItem>>(toastManager.getToasts())
  const [activeId, setActiveId] = useState<string | undefined>()
  const hoverRef = useRef(false)
  const routesAllToNotchRef = useRef(routesAllToNotch)
  routesAllToNotchRef.current = routesAllToNotch
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const listRef = useRef(toasts)
  const latestRef = useRef<string | undefined>(undefined)
  const activeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handlersCache = useRef(
    new Map<
      string,
      {
        enter: React.MouseEventHandler
        leave: React.MouseEventHandler
        dismiss: () => void
      }
    >(),
  )

  const clearAllTimers = useCallback(() => {
    for (const t of timersRef.current.values()) clearTimeout(t)
    timersRef.current.clear()
  }, [])

  const schedule = useCallback((items: Array<ToastItem>) => {
    if (hoverRef.current) return
    for (const item of items) {
      if (item.exiting) continue
      const key = timeoutKey(item)
      if (timersRef.current.has(key)) continue
      const dur = resolveToastDuration(item, routesAllToNotchRef.current)
      if (dur === null || dur <= 0) continue
      timersRef.current.set(
        key,
        setTimeout(() => toastManager.dismiss(item.id, 'timeout'), dur),
      )
    }
  }, [])

  useEffect(() => {
    const unsub = toastManager.subscribe(setToasts)
    return () => {
      unsub()
      clearAllTimers()
    }
  }, [clearAllTimers])

  useEffect(() => {
    listRef.current = toasts
    const toastKeys = new Set(toasts.map(timeoutKey))
    const toastIds = new Set(toasts.map((t) => t.id))

    for (const [key, timer] of timersRef.current) {
      if (!toastKeys.has(key)) {
        clearTimeout(timer)
        timersRef.current.delete(key)
      }
    }

    for (const id of handlersCache.current.keys()) {
      if (!toastIds.has(id)) handlersCache.current.delete(id)
    }

    schedule(toasts)
  }, [toasts, schedule])

  const handleViewportEnter = useCallback(() => {
    if (hoverRef.current) return
    hoverRef.current = true
    clearAllTimers()
  }, [clearAllTimers])

  const handleViewportLeave = useCallback(() => {
    if (!hoverRef.current) return
    hoverRef.current = false
    schedule(listRef.current)
    // Reset activeId to latest when mouse fully leaves viewport
    if (activeDebounceRef.current) clearTimeout(activeDebounceRef.current)
    setActiveId(undefined)
  }, [schedule])

  useEffect(() => {
    toastManager.setMiniNotched(routesAllToNotch)
  }, [routesAllToNotch])

  useEffect(() => {
    toastManager.setRoutesAllToNotch(routesAllToNotch)
    // The corner empties without a mouse leave, so a hover it held would pause the notch's timers for good
    if (routesAllToNotch) handleViewportLeave()
  }, [routesAllToNotch, handleViewportLeave])

  // The timers live here, so reading a toast in the notch has to pause them here too
  useEffect(() => {
    if (!routesAllToNotch || !isNotchHovered) return
    handleViewportEnter()
    // Resumes on the hover ending or the toasts returning to the corner alike
    return handleViewportLeave
  }, [routesAllToNotch, isNotchHovered, handleViewportEnter, handleViewportLeave])

  const visibleToasts = useMemo(
    () => toasts.filter((toast) => isShownInViewport(toast, routesAllToNotch, routesAllToNotch)),
    [routesAllToNotch, toasts],
  )

  const latest = useMemo(() => {
    for (let i = visibleToasts.length - 1; i >= 0; i--) {
      if (!visibleToasts[i].exiting) return visibleToasts[i].id
    }
    return undefined
  }, [visibleToasts])

  useEffect(() => {
    latestRef.current = latest
    setActiveId(latest)
  }, [latest])

  const getHandlers = useCallback((toastId: string) => {
    let cached = handlersCache.current.get(toastId)
    if (cached) return cached

    cached = {
      enter: () => {
        if (activeDebounceRef.current) {
          clearTimeout(activeDebounceRef.current)
          activeDebounceRef.current = null
        }
        setActiveId((prev) => (prev === toastId ? prev : toastId))
      },
      leave: () => {
        // Debounce activeId reset — if another toast's enter fires within 150ms, it cancels this
        if (activeDebounceRef.current) clearTimeout(activeDebounceRef.current)
        activeDebounceRef.current = setTimeout(() => {
          activeDebounceRef.current = null
          setActiveId((prev) => (prev === latestRef.current ? prev : latestRef.current))
        }, ACTIVE_DEBOUNCE_MS)
      },
      dismiss: () => toastManager.dismiss(toastId, 'dismiss button'),
    }
    handlersCache.current.set(toastId, cached)
    return cached
  }, [])

  const sidepanelState = useSidepanelState()
  const sidepanelWidth = useSidepanelRenderedWidth()
  const side = useSidepanelSide()
  // Toasts on the panel's side shift inward (away from the panel) while it's open.
  const onPanelSide = position.includes(side)
  const shouldShift = onPanelSide && sidepanelState === 'open'
  const shiftX = toastSidepanelShiftX(sidepanelVisibleWidth(sidepanelWidth), side)

  const pill = pillAlign(position)
  const expand = expandDir(position)

  if (visibleToasts.length === 0) return null

  return (
    <section
      data-toast-viewport=""
      data-position={position}
      className={twMerge(
        'pointer-events-none fixed z-50 flex max-w-[calc(100vw-1.5rem)] gap-3 py-3',
        position.startsWith('bottom') ? 'bottom-0 flex-col' : 'top-0 flex-col-reverse',
        position === 'top-center' && 'pt-5',
        position.includes('right')
          ? 'right-0 items-end'
          : position.includes('center')
            ? 'left-1/2 -translate-x-1/2 items-center'
            : 'left-0 items-start',
      )}
      style={{
        top: position.startsWith('top') ? menuBarHeight : undefined,
        paddingInline: TOAST_VIEWPORT_PADDING,
        transform: shouldShift ? `translateX(${shiftX}px)` : undefined,
        transition: onPanelSide ? 'transform 0.2s ease' : undefined,
      }}
      aria-live="polite"
      onMouseEnter={handleViewportEnter}
      onMouseLeave={handleViewportLeave}
    >
      {visibleToasts.map((item) => {
        const h = getHandlers(item.id)
        return (
          <ToastPill
            key={item.id}
            id={item.id}
            title={item.title}
            headerExtra={item.headerExtra}
            description={item.description}
            position={pill}
            expand={expand}
            icon={item.icon}
            fill={item.fill}
            styles={item.styles}
            roundness={item.roundness}
            exiting={item.exiting}
            autoExpandDelayMs={item.autoExpandDelayMs}
            autoCollapseDelayMs={item.autoCollapseDelayMs}
            keepExpandedOnRefresh={item.keepExpandedOnRefresh}
            refreshKey={item.instanceId}
            stableHeaderKey={item.stableHeaderKey}
            canExpand={activeId === item.id}
            onMouseEnter={h.enter}
            onMouseLeave={h.leave}
            onDismiss={h.dismiss}
            onHeaderClick={item.onHeaderClick}
          />
        )
      })}
    </section>
  )
})
